import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useDialog } from '../../context/DialogContext'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card'
import { ArrowLeft, Buildings, MapPin, Envelope, Phone, Globe, Calendar, Briefcase } from '@phosphor-icons/react'
import { getCompanyById } from '../../services/companies'

export default function CompanyDetailsPage() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { showError } = useDialog()
  const companyType = searchParams.get('type') as 'shipper' | 'freight-forwarder'
  const [company, setCompany] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadCompany() {
      if (!id || !companyType) return

      try {
        const data = await getCompanyById(id, companyType)
        setCompany(data)
      } catch (error) {
        console.error('Error loading company:', error)
        showError('Erro', 'Erro ao carregar empresa')
      } finally {
        setLoading(false)
      }
    }
    loadCompany()
  }, [id, companyType])

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Carregando...</div>
        </div>
      </AppLayout>
    )
  }

  if (!company) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Empresa não encontrada</div>
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/master/companies')}
                className="gap-2"
              >
                <ArrowLeft size={18} />
                Voltar
              </Button>
              <div className="h-6 w-px bg-gray-200" />
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Buildings size={24} className="text-emerald-600" weight="duotone" />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold text-gray-900">
                    {company.tradingName || company.name}
                  </h1>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      companyType === 'shipper'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-purple-100 text-purple-700'
                    }`}>
                      {companyType === 'shipper' ? 'Embarcador' : 'Agente de Carga'}
                    </span>
                    <span className="text-sm text-gray-500">ID: {company.companyId.slice(0, 8)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-2 gap-6">
          {/* Company Information */}
          <Card>
            <CardHeader>
              <CardTitle>Informações da Empresa</CardTitle>
              <CardDescription>Dados cadastrais e de contato</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {company.tradingName && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Nome Fantasia</label>
                  <p className="text-gray-900 mt-1">{company.tradingName}</p>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-gray-500">Razão Social</label>
                <p className="text-gray-900 mt-1">{company.name}</p>
              </div>

              {company.primaryIdentifier && (
                <div>
                  <label className="text-sm font-medium text-gray-500">
                    {company.primaryIdentifierType || 'Documento'}
                  </label>
                  <p className="text-gray-900 mt-1">{company.primaryIdentifier}</p>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-gray-500">País</label>
                <div className="flex items-center gap-2 mt-1">
                  <Globe size={16} className="text-gray-400" />
                  <p className="text-gray-900">{company.countryCode}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle>Contato</CardTitle>
              <CardDescription>Informações de contato da empresa</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {company.email && (
                <div>
                  <label className="text-sm font-medium text-gray-500">E-mail</label>
                  <div className="flex items-center gap-2 mt-1">
                    <Envelope size={16} className="text-gray-400" />
                    <a href={`mailto:${company.email}`} className="text-emerald-600 hover:underline">
                      {company.email}
                    </a>
                  </div>
                </div>
              )}

              {company.phone && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Telefone</label>
                  <div className="flex items-center gap-2 mt-1">
                    <Phone size={16} className="text-gray-400" />
                    <a href={`tel:${company.phone}`} className="text-emerald-600 hover:underline">
                      {company.phone}
                    </a>
                  </div>
                </div>
              )}

              {company.address && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Endereço</label>
                  <div className="flex items-start gap-2 mt-1">
                    <MapPin size={16} className="text-gray-400 mt-0.5" />
                    <div className="text-gray-900">
                      {typeof company.address === 'string'
                        ? company.address
                        : JSON.stringify(company.address, null, 2)}
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-gray-500">Criado em</label>
                <div className="flex items-center gap-2 mt-1">
                  <Calendar size={16} className="text-gray-400" />
                  <p className="text-gray-900">
                    {new Date(company.createdAt).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tenant Information */}
          <Card>
            <CardHeader>
              <CardTitle>Tenant</CardTitle>
              <CardDescription>Organização à qual esta empresa pertence</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Briefcase size={20} className="text-blue-600" weight="duotone" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Tenant ID</p>
                    <p className="text-sm text-gray-500">{company.tenantId}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/master/tenants/${company.tenantId}`)}
                >
                  Ver Tenant
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Actor Information */}
          <Card>
            <CardHeader>
              <CardTitle>Informações Técnicas</CardTitle>
              <CardDescription>IDs e referências do sistema</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-500">Company ID</label>
                <p className="text-gray-900 mt-1 font-mono text-xs">{company.companyId}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-500">Actor ID</label>
                <p className="text-gray-900 mt-1 font-mono text-xs">{company.actorId}</p>
              </div>

              {company.document && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Document (Legacy)</label>
                  <p className="text-gray-900 mt-1">{company.document}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </AppLayout>
  )
}
